import React, {useRef, useState} from 'react';
import {Button, Form, Icon, Input, Loader, Message} from 'semantic-ui-react';
import * as XLSX from 'xlsx';
import RolDropdown from '../../componentes/RolDropdown';
import FormStyles from '../forms/form.module.css';
import Styles from './nuevoEmpleado.module.css';
import dbApp, {getCompanyCollection} from '../../firebase';

const encabezadosEsperados = ['nombre', 'correo', 'rol', 'telefono'];
const normalizar = value => String(value || '').trim().toLowerCase();

const NuevoEmpleado = ({empMax, onCancel}) => {
  const [nombre, setNombre] = useState('');
  const [mail, setMail] = useState('');
  const [rol, setRol] = useState('user');
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mostrarImportacion, setMostrarImportacion] = useState(false);
  const fileInput = useRef(null);

  const crearEmpleado = async ({nombre, correo, rol, telefono = '', activo}) => {
    const currentUser = dbApp.auth().currentUser;
    if (!currentUser) throw new Error('Tu sesión expiró. Inicia sesión nuevamente.');
    await currentUser.getIdToken(true);
    const createEmployee = dbApp.functions('us-central1').httpsCallable('createEmployee');
    await createEmployee({nombre, correo, rol, telefono, activo});
  };

  const writeNew = async event => {
    event.preventDefault();
    if (!nombre || !mail || !rol) return setMessage({success: false, text: 'Llena todos los campos.'});
    setLoading(true);
    try {
      const empleados = await getCompanyCollection('Empleados');
      const existente = await empleados.where('Correo', '==', mail.trim()).get();
      if (!existente.empty) throw new Error('Este correo ya existe.');
      const activos = await empleados.where('activo', '==', 'si').get();
      const activo = activos.size < empMax ? 'si' : 'no';
      await crearEmpleado({nombre: nombre.trim(), correo: mail.trim(), rol, activo});
      setNombre(''); setMail(''); setRol('user');
      setMessage({success: true, text: activo === 'si' ? 'Empleado creado como activo.' : 'Empleado creado como inactivo: ya se alcanzó el límite de activos.'});
    } catch (error) {
      setMessage({success: false, text: error.message || 'No fue posible crear el empleado.'});
    } finally { setLoading(false); }
  };

  const descargarPlantilla = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([encabezadosEsperados, ['Ejemplo: Juan Pérez', 'juan@ejemplo.com', 'user', '5551234567']]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Empleados');
    XLSX.writeFile(workbook, 'plantilla-empleados.xlsx');
  };

  const importarArchivo = async event => {
    const input = event.target;
    const file = input.files[0];
    if (!file) return;
    setLoading(true); setMessage(null);
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), {type: 'array'});
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], {header: 1, defval: ''});
      const headers = (rows[0] || []).slice(0, encabezadosEsperados.length).map(normalizar);
      if (headers.some((header, index) => header !== encabezadosEsperados[index])) throw new Error('Los encabezados deben ser: nombre, correo, rol, telefono.');
      const empleados = await getCompanyCollection('Empleados');
      const existentes = await empleados.get();
      const correos = new Set(existentes.docs.map(doc => normalizar(doc.data().Correo)));
      let importados = 0; let duplicados = 0;
      for (const row of rows.slice(1)) {
        const empleadoNombre = String(row[0] || '').trim();
        const correo = String(row[1] || '').trim();
        if (!empleadoNombre || normalizar(empleadoNombre).startsWith('ejemplo:')) continue;
        if (!correo || correos.has(normalizar(correo))) { duplicados++; continue; }
        const empleadoRol = String(row[2] || 'user').trim() || 'user';
        await crearEmpleado({nombre: empleadoNombre, correo, rol: empleadoRol, telefono: String(row[3] || '').trim(), activo: 'no'});
        correos.add(normalizar(correo)); importados++;
      }
      setMessage({success: true, text: `${importados} empleados importados; ${duplicados} duplicados ignorados. Todos fueron creados como inactivos.`});
    } catch (error) {
      setMessage({success: false, text: error.message || 'No fue posible importar el archivo.'});
    } finally { input.value = ''; setLoading(false); }
  };

  return <form className={FormStyles.form} onSubmit={writeNew}>
    <Form.Field className={FormStyles.fieldRow}>
      <label className={FormStyles.fieldLabel}>Nombre</label>
      <div className={FormStyles.fieldContent}><Input fluid value={nombre} onChange={event => setNombre(event.target.value)} placeholder='Nombre completo' /></div>
    </Form.Field>
    <Form.Field className={FormStyles.fieldRow}>
      <label className={FormStyles.fieldLabel}>Correo</label>
      <div className={FormStyles.fieldContent}><Input fluid type='email' value={mail} onChange={event => setMail(event.target.value)} placeholder='correo@ejemplo.com' /></div>
    </Form.Field>
    <Form.Field className={FormStyles.fieldRow}>
      <label className={FormStyles.fieldLabel}>Rol</label>
      <div className={FormStyles.fieldContent}><RolDropdown placeholder='Selecciona un rol' value={rol} onChange={(event, {value}) => setRol(value)} all={1} /></div>
    </Form.Field>
    {mostrarImportacion && <div className={Styles.importPanel}>
      <strong>Importar empleados desde Excel</strong>
      <p>La plantilla no incluye la columna activo; todos los empleados importados se crean inactivos.</p>
      <div className={Styles.importOptions}>
        <Button type='button' basic onClick={descargarPlantilla}><Icon name='download' />Descargar plantilla</Button>
        <Button type='button' basic loading={loading} onClick={() => fileInput.current.click()}><Icon name='file excel outline' />Seleccionar archivo</Button>
        <input ref={fileInput} className={Styles.fileInput} type='file' accept='.xls,.xlsx' onChange={importarArchivo} />
      </div>
    </div>}
    {message && <Message positive={message.success} negative={!message.success} size='small'>{message.text}</Message>}
    <div className={FormStyles.actions}>
      <Button type='button' basic onClick={onCancel}>Cancelar</Button>
      <Button type='submit' positive disabled={loading}>Agregar</Button>
    </div>
    {loading && <Loader active inline='centered' />}
  </form>;
};

export default NuevoEmpleado;
